/**
 * Owner / Agency-Founder Command Center (Task 204).
 *
 * The executive landing surface for agency leadership (owner /
 * marketing_manager). Rendered by `AppLanding` ONLY when
 * `isLeadership(me)` is true — all other roles keep their Task 200
 * behavior (this component never gates data; it is display-only and
 * leadership-gated upstream).
 *
 * Design reference: design_handoff_super_agency/screens/23-owner-command-center-v2.html
 * (recreated with the app's existing tokens + React/Vite patterns; not a
 * blind paste). RTL, cream/ink/tangerine brand.
 *
 * Data posture: real data is used where the existing `me-context` /
 * `list-clients` surface already provides it (active client count, the
 * client list, status-derived decision signals). Executive metrics that
 * have no live endpoint yet (MRR, profit margin, net retention, paid-media
 * exceptions, team load, account-manager assignment) render as honest
 * "—" placeholders / empty states labelled «قيد الربط» — the layout is
 * ready for real data later. No fabricated figures, no new endpoints, no
 * fake destructive actions (every action is a real route link).
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { MeContext } from '../api/types';
import { useClientScope } from '../context/ClientScopeProvider';
import {
  avatarColor,
  buildDecisions,
  clientStatusLabelAr,
  initial,
  SOON,
  statusBadgeClass,
  statusRisk,
  statusWeight,
  toArabicDigits,
} from './ownerCommandCenter.logic';

// ── Inline icon set (24×24, currentColor stroke) ─────────────────────
type IconName =
  | 'search'
  | 'plus'
  | 'bell'
  | 'bolt'
  | 'money'
  | 'wallet'
  | 'users'
  | 'heart'
  | 'pause'
  | 'chev'
  | 'clock'
  | 'check'
  | 'megaphone'
  | 'user-plus'
  | 'palette'
  | 'video'
  | 'chart'
  | 'package'
  | 'briefcase'
  | 'swap'
  | 'target';

function Icon({ name, className }: { readonly name: IconName; readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      {iconBody(name)}
    </svg>
  );
}

function iconBody(name: IconName): ReactNode {
  switch (name) {
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" strokeLinecap="round" />
        </>
      );
    case 'plus':
      return <path d="M12 5v14M5 12h14" strokeLinecap="round" />;
    case 'bell':
      return (
        <path
          d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M13.7 21a2 2 0 0 1-3.4 0"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'bolt':
      return <path d="M13 2L3 14h8l-1 8 10-12h-8z" strokeLinejoin="round" />;
    case 'money':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path
            d="M15 9.5a3 3 0 0 0-3-1.5c-1.7 0-3 .9-3 2s1.3 2 3 2 3 .9 3 2-1.3 2-3 2a3 3 0 0 1-3-1.5M12 6v1.5M12 16.5V18"
            strokeLinecap="round"
          />
        </>
      );
    case 'wallet':
      return (
        <path
          d="M3 7a2 2 0 0 1 2-2h12v3M3 7v10a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-3M3 7h17a1 1 0 0 1 1 1v3M17 13h.01"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'users':
      return (
        <>
          <circle cx="9" cy="8" r="4" />
          <path
            d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6M17 11a4 4 0 0 0 0-8M22 21c0-2.8-1.9-5.1-4.5-5.8"
            strokeLinecap="round"
          />
        </>
      );
    case 'heart':
      return (
        <path
          d="M12 20s-7-4.5-9.5-9C1 8 2.5 5 5.5 5 8 5 9 7 12 9c3-2 4-4 6.5-4C21.5 5 23 8 21.5 11 19 15.5 12 20 12 20z"
          strokeLinejoin="round"
        />
      );
    case 'pause':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M10 9v6M14 9v6" strokeLinecap="round" />
        </>
      );
    case 'chev':
      return <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />;
    case 'clock':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" strokeLinecap="round" />
        </>
      );
    case 'check':
      return (
        <path
          d="M9 11l3 3 8-8M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'megaphone':
      return (
        <path
          d="M3 11v2a1 1 0 0 0 1 1h3l7 5V5L7 10H4a1 1 0 0 0-1 1zM18 8a5 5 0 0 1 0 8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'user-plus':
      return (
        <>
          <circle cx="9" cy="8" r="4" />
          <path d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6M19 8v6M22 11h-6" strokeLinecap="round" />
        </>
      );
    case 'palette':
      return (
        <>
          <path
            d="M12 3a9 9 0 1 0 0 18 3 3 0 0 0 2-5 3 3 0 0 1 2-5h1a4 4 0 0 0 4-4 9 9 0 0 0-9-4z"
            strokeLinejoin="round"
          />
        </>
      );
    case 'video':
      return (
        <>
          <rect x="2" y="6" width="14" height="12" rx="2" />
          <path d="M16 10l6-3v10l-6-3z" strokeLinejoin="round" />
        </>
      );
    case 'chart':
      return <path d="M3 21V5M3 21h18M7 16v-6M12 16v-9M17 16v-3" strokeLinecap="round" />;
    case 'package':
      return (
        <path
          d="M12 2l9 5v10l-9 5-9-5V7z M12 2v20 M3 7l9 5 9-5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'briefcase':
      return (
        <>
          <rect x="3" y="7" width="18" height="14" rx="2" />
          <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18" strokeLinecap="round" />
        </>
      );
    case 'swap':
      return (
        <path
          d="M7 4L3 8l4 4M3 8h13M17 20l4-4-4-4M21 16H8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'target':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
        </>
      );
    default:
      return null;
  }
}

interface OwnerCommandCenterProps {
  readonly me: MeContext;
}

export function OwnerCommandCenter({ me }: OwnerCommandCenterProps) {
  const { clients, status: scopeStatus } = useClientScope();
  const ready = scopeStatus === 'ready';
  const ownerName = me.profile?.full_name ?? me.profile?.email ?? 'القائد';
  const orgName = me.organization?.name ?? 'وكالتك';

  const activeCount = clients.filter((c) => c.status === 'active').length;
  const pausedCount = clients.filter((c) => c.status === 'paused').length;

  // Decisions derived from real client status (honest signals only).
  const decisions = buildDecisions(clients);

  // Client health rows — needs-attention first, then active. Cap at 8.
  const tableRows = [...clients]
    .sort((a, b) => statusWeight(b.status) - statusWeight(a.status))
    .slice(0, 8);

  return (
    <div className="occ" dir="rtl">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div className="occ-top">
        <div className="occ-search">
          <input type="search" placeholder="ابحث عن عميل، حملة، موظف، تقرير…" aria-label="بحث" />
          <Icon name="search" />
        </div>
        <div className="occ-top-actions">
          <div className="occ-range" role="group" aria-label="النطاق الزمني">
            <button type="button">٧ أيام</button>
            <button type="button" className="active" aria-pressed="true">
              هذا الشهر
            </button>
            <button type="button">الربع</button>
          </div>
          <Link to="/app/clients?new=1" className="occ-create">
            <Icon name="plus" />
            إنشاء
          </Link>
          <Link
            to="/app/notifications"
            className="occ-iconbtn"
            title="الإشعارات"
            aria-label="الإشعارات"
          >
            <Icon name="bell" />
            <span className="occ-dot" />
          </Link>
        </div>
      </div>

      {/* ── Page header ─────────────────────────────────────────── */}
      <header className="occ-head">
        <h1 className="h-page">
          مركز{' '}
          <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
            القيادة
          </em>
        </h1>
        <p className="occ-sub">
          أهلاً {ownerName} — مساحة {orgName}.{' '}
          {decisions.length > 0 ? (
            <>
              <b>{decisions.length} قرار</b> بحاجة إلى متابعتك اليوم.
            </>
          ) : (
            'لا توجد قرارات عاجلة الآن.'
          )}{' '}
          <span className="occ-note-inline">
            بعض المؤشّرات التنفيذية قيد الربط بالبيانات المباشرة.
          </span>
        </p>
      </header>

      {/* ── Executive KPI strip ─────────────────────────────────── */}
      <div className="occ-pulse">
        <span className="occ-blob" />
        <Kpi icon="money" cap="الإيراد الشهري المتكرر" value={SOON} note="قيد الربط" />
        <Kpi icon="wallet" cap="هامش الربح الإجمالي" value={SOON} note="قيد الربط" />
        <Kpi
          icon="users"
          cap="العملاء النشطون"
          value={ready ? toArabicDigits(activeCount) : SOON}
          note={ready ? `من ${toArabicDigits(clients.length)} إجمالاً` : '…'}
        />
        <Kpi
          icon="pause"
          cap="عملاء في خطر"
          value={ready ? toArabicDigits(pausedCount) : SOON}
          note="متوقّفون حالياً"
          risk
        />
        <Kpi icon="heart" cap="صافي الاحتفاظ" value={SOON} note="قيد الربط" />
      </div>

      {/* ── Decisions centerpiece ───────────────────────────────── */}
      <section className="occ-decisions">
        <div className="occ-dec-head">
          <span className="occ-dh-ic">
            <Icon name="bolt" />
          </span>
          <span className="occ-dh-t">
            قرارات تحتاج <em>إجراءك</em> اليوم
          </span>
          <span className="occ-dh-c">
            {decisions.length > 0 ? `${toArabicDigits(decisions.length)} بنود` : 'لا بنود'}
          </span>
        </div>
        {decisions.length > 0 ? (
          decisions.map((d) => (
            <div key={d.key} className={`occ-dec ${d.tone}`}>
              <span className={`occ-sev ${d.sev}`}>
                <span className="occ-sd" />
                {d.sevLabel}
              </span>
              <span className="occ-d-ic">
                <Icon name={d.icon} />
              </span>
              <div className="occ-d-body">
                <div className="occ-d-t">{d.title}</div>
                <div className="occ-d-why">{d.why}</div>
              </div>
              <Link to={d.to} className="occ-d-act">
                {d.action}
                <Icon name="chev" />
              </Link>
            </div>
          ))
        ) : (
          <div className="occ-empty">
            ستظهر هنا القرارات التنفيذية المرتّبة حسب الأولوية — التجديدات، الحسابات المتوقّفة،
            الموافقات المعلّقة، والإسنادات — فور توفّر إشاراتها. لا يوجد ما يستدعي إجراءً عاجلاً
            الآن.
          </div>
        )}
      </section>

      {/* ── Agency health cards ─────────────────────────────────── */}
      <div className="occ-strip-label">
        <span className="lead">صحة الوكالة</span>
      </div>
      <div className="occ-health">
        <HealthCard
          to="/app/clients"
          icon="pause"
          tone="ic-rose"
          flag={ready && pausedCount > 0}
          value={ready ? toArabicDigits(pausedCount) : SOON}
          label="عملاء في خطر"
          note="حسابات متوقّفة حالياً"
        />
        <HealthCard
          to="/app/team"
          icon="user-plus"
          tone="ic-amber"
          value={SOON}
          label="عملاء بلا مدير حساب"
          note="قيد الربط بالبيانات"
        />
        <HealthCard
          to="/app/clients"
          icon="clock"
          tone="ic-rose"
          value={SOON}
          label="عمل متأخر يؤثّر على العملاء"
          note="قيد الربط بالبيانات"
        />
        <HealthCard
          to="/app/clients"
          icon="check"
          tone="ic-sky"
          value={SOON}
          label="موافقات معلّقة"
          note="قيد الربط بالبيانات"
        />
        <HealthCard
          to="/app/paid-media"
          icon="megaphone"
          tone="ic-plum"
          value={SOON}
          label="حملات تحتاج تدخّل"
          note="قيد الربط بالبيانات"
        />
      </div>

      {/* ── Main grid ───────────────────────────────────────────── */}
      <div className="occ-grid">
        <div>
          {/* Client health table */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                صحة <em>العملاء</em>
              </div>
              <Link to="/app/clients" className="occ-card-link">
                كل العملاء <Icon name="chev" />
              </Link>
            </div>
            {tableRows.length > 0 ? (
              <table className="occ-ctable">
                <thead>
                  <tr>
                    <th>العميل</th>
                    <th className="e">القيمة الشهرية</th>
                    <th className="c">الحالة</th>
                    <th className="c">الاتجاه</th>
                    <th className="c">الخطر</th>
                    <th>الإجراء التالي</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((c) => {
                    const risk = statusRisk(c.status);
                    return (
                      <tr key={c.id}>
                        <td>
                          <div className="occ-cl-cell">
                            <span className="occ-cl-av" style={{ background: avatarColor(c.id) }}>
                              {initial(c.name)}
                            </span>
                            <span className="occ-cl-nm">{c.name}</span>
                          </div>
                        </td>
                        <td className="e mono">{SOON}</td>
                        <td className="c">
                          <span className={`occ-badge ${statusBadgeClass(c.status)}`}>
                            {clientStatusLabelAr(c.status)}
                          </span>
                        </td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c">
                          <span className={`occ-risk ${risk.cls}`}>
                            <span className="rd" />
                            {risk.label}
                          </span>
                        </td>
                        <td>
                          <Link to={`/app/clients/${c.id}/overview`} className="occ-next">
                            فتح المساحة <Icon name="chev" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="occ-empty">
                {ready
                  ? 'لا يوجد عملاء بعد. أضف أول عميل لبدء متابعة صحتهم هنا.'
                  : 'جارٍ تحميل قائمة العملاء…'}
              </div>
            )}
          </section>

          {/* Paid media exceptions */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                استثناءات <em>الإعلانات</em>
              </div>
              <Link to="/app/paid-media" className="occ-card-link">
                كل الحملات <Icon name="chev" />
              </Link>
            </div>
            <div className="occ-empty">
              تعرض هذه البطاقة <b>الاستثناءات فقط</b> — العائد الضعيف، الإنفاق الزائد، الحسابات
              المنقطعة، والمزامنة المتأخّرة — وليست لوحة حملات كاملة. ستظهر فور ربط مؤشّرات الأداء
              المباشرة.{' '}
              <Link to="/app/paid-media" className="occ-inline-link">
                افتح الإعلانات المدفوعة ←
              </Link>
            </div>
          </section>
        </div>

        <div>
          {/* Team load */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                حِمل <em>الفريق</em>
              </div>
              <Link to="/app/team" className="occ-card-link">
                الفريق <Icon name="chev" />
              </Link>
            </div>
            <div className="occ-team-roles">
              {[
                { icon: 'users' as IconName, tone: 'ic-sky', label: 'مديرو الحسابات' },
                { icon: 'megaphone' as IconName, tone: 'ic-amber', label: 'مشترو الإعلانات' },
                { icon: 'palette' as IconName, tone: 'ic-plum', label: 'المصممون' },
                { icon: 'video' as IconName, tone: 'ic-sage', label: 'منتجو الفيديو' },
              ].map((r) => (
                <div key={r.label} className="occ-trole">
                  <span className={`occ-ti ${r.tone}`}>
                    <Icon name={r.icon} />
                  </span>
                  <span className="occ-tn">{r.label}</span>
                  <span className="occ-tover">{SOON}</span>
                </div>
              ))}
            </div>
            <div className="occ-empty sm">
              سيظهر هنا حِمل كل فريق ومؤشّرات الاختناق والمهام المتأخّرة فور ربط بيانات المهام.
            </div>
          </section>

          {/* Clients without account manager */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                عملاء بلا <em>مدير حساب</em>
              </div>
            </div>
            <div className="occ-empty sm">
              سيظهر هنا العملاء الذين لم يُسنَد إليهم مدير حساب بعد، مع إجراء إسناد سريع. الإسناد
              يتم من صفحة الفريق.
              <div style={{ marginTop: 12 }}>
                <Link to="/app/team" className="occ-assign">
                  <Icon name="user-plus" /> إدارة الإسناد
                </Link>
              </div>
            </div>
          </section>

          {/* Quick actions */}
          <div className="occ-qa-card">
            <div className="occ-qa-label">إجراءات سريعة</div>
            <div className="occ-qa-row">
              <Link to="/app/clients?new=1" className="occ-qa">
                <Icon name="user-plus" /> إضافة عميل
              </Link>
              <Link to="/app/team" className="occ-qa">
                <Icon name="briefcase" /> دعوة موظف
              </Link>
              <Link to="/app/team" className="occ-qa">
                <Icon name="swap" /> إسناد مدير حساب
              </Link>
              <Link to="/app/clients" className="occ-qa">
                <Icon name="check" /> مراجعة الموافقات
              </Link>
              <Link to="/app/reports" className="occ-qa">
                <Icon name="chart" /> إنشاء تقرير
              </Link>
              {/* Task 300A — this opens the agency-to-client catalogue,
                  so it carries that layer's name. The SaaS billing word
                  would point an owner at the wrong page. */}
              <Link to="/app/services" className="occ-qa">
                <Icon name="package" /> الخدمات والباقات
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({
  icon,
  cap,
  value,
  note,
  risk,
}: {
  readonly icon: IconName;
  readonly cap: string;
  readonly value: string;
  readonly note: string;
  readonly risk?: boolean;
}) {
  return (
    <div className="occ-pl">
      <div className="occ-cap">
        <Icon name={icon} />
        {cap}
      </div>
      <div className={`occ-big${risk ? ' risk' : ''}`}>{value}</div>
      <div className="occ-delta">{note}</div>
    </div>
  );
}

function HealthCard({
  to,
  icon,
  tone,
  value,
  label,
  note,
  flag,
}: {
  readonly to: string;
  readonly icon: IconName;
  readonly tone: string;
  readonly value: string;
  readonly label: string;
  readonly note: string;
  readonly flag?: boolean;
}) {
  return (
    <Link to={to} className={`occ-hcard${flag ? ' flag' : ''}`}>
      <div className="occ-htop">
        <span className={`occ-h-ic ${tone}`}>
          <Icon name={icon} />
        </span>
        <Icon name="chev" className="occ-h-chev" />
      </div>
      <div className="occ-val">{value}</div>
      <div className="occ-lbl">{label}</div>
      <div className="occ-cnote">{note}</div>
    </Link>
  );
}
